/** Broad Yahoo-style categories that must NOT appear as the title prefix. */
const BROAD_TITLE_CATEGORIES = new Set([
  '音楽',
  '映画、ビデオ',
  '映画・ビデオ',
  'おもちゃ、ゲーム',
  'おもちゃ・ゲーム',
  '家電、AV、カメラ',
  '家電・AV・カメラ',
  'コンピュータ',
  '本、雑誌',
  '本・雑誌',
  'スポーツ、レジャー',
  'スポーツ・レジャー',
  'ホビー、カルチャー',
  'ホビー・カルチャー',
  '住まい、インテリア',
  '住まい・インテリア',
  'アンティーク、コレクション',
  'アンティーク・コレクション',
  '自動車、オートバイ',
  '自動車・オートバイ',
  '事務、店舗用品',
  '事務・店舗用品',
  '携帯電話、スマートフォン',
  '携帯電話・スマートフォン',
  'ファッション',
  'コスメ、香水',
  'コスメ・香水',
  '食品、飲料',
  '食品・飲料',
  'キッズ、ベビー',
  'キッズ・ベビー',
]);

function stripAdultMarker(value: string): string {
  return value.startsWith('■') ? value.slice(1).trim() : value;
}

function isBroadTitleCategory(value: string): boolean {
  const normalized = stripAdultMarker(value.trim());
  if (BROAD_TITLE_CATEGORIES.has(normalized)) return true;
  return [...BROAD_TITLE_CATEGORIES].some(
    (broad) => normalized.startsWith(broad) && normalized.length <= broad.length + 4
  );
}

/**
 * Extract a product-noun hint from a Yahoo category path for title generation.
 * e.g. "音楽/CD/その他" → "CD" (not the broad top-level segment).
 */
export function buildTitleCategoryHint(category: string): string {
  const trimmed = category.trim();
  if (!trimmed) return '';

  const segments = trimmed
    .replace(/\s*>\s*/g, '/')
    .replace(/\/+/g, '/')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length === 0) return '';

  const endsWithSonota = segments[segments.length - 1] === 'その他';

  if (endsWithSonota && segments.length >= 3) {
    return segments[1];
  }

  if (endsWithSonota && segments.length === 2) {
    return segments[0];
  }

  if (segments.length >= 2) {
    return segments[1];
  }

  return segments[0];
}

/**
 * Remove broad category prefixes (e.g. 音楽) when a specific product noun follows (e.g. CD).
 * Prevents titles like "音楽 / CD / ..." and keeps "CD / ...".
 */
export function normalizeTitleCategoryPrefix(title: string): string {
  const parts = title
    .split(' / ')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    return title.trim();
  }

  while (parts.length >= 2 && isBroadTitleCategory(parts[0])) {
    parts.shift();
  }

  const deduped: string[] = [];
  for (const part of parts) {
    const prev = deduped[deduped.length - 1];
    if (!prev || prev.toLowerCase() !== part.toLowerCase()) {
      deduped.push(part);
    }
  }

  return deduped.join(' / ').trim();
}

export function cleanTitle(title: string): string {
  const sanitized = title
    .replace(/^["']|["']$/g, '')
    .replace(/^\s*【.*?】\s*/, '')
    .replace(/\s*【.*?】\s*$/, '')
    .replace(/\s+/g, ' ')
    .replace(/[^\S\r\n]+/g, ' ')
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .replace(/状態[：:].+?(?=[、。]|$)/g, '')
    .replace(/コンディション[：:].+?(?=[、。]|$)/g, '')
    .replace(/使用感[：:].+?(?=[、。]|$)/g, '')
    .trim();

  return normalizeTitleCategoryPrefix(sanitized).slice(0, 65).trim();
}

export function validateTitle(title: string): boolean {
  if (!title) return false;
  if (title.length > 65) return false;
  if (/^[\s\d]+$/.test(title)) return false;
  if (/状態[:：]|コンディション[:：]|使用感[:：]/.test(title)) return false;
  return true;
}
